import { Input as AntInput, Modal } from "antd";
import { WrappedFormUtils } from "antd/lib/form/Form";
import { InputProps } from "antd/lib/input";
import { debounce, isEmpty } from "lodash";
import * as React from "react";
import styled, { createGlobalStyle, css } from "styled-components";

import { Button } from "./Button";
import { Icon } from "./Icon";
import { isTablet, media, space } from "./common";
import { theme } from "./theme";

const KEYBOARD = 13;

const Wrapper = styled.div`
  width: 100%;
  .ant-select.ant-select-combobox {
    .ant-select-search__field {
      z-index: 0;
    }
  }
  input.ant-input.ant-input-disabled:hover {
    border-color: #d9d9d9;
  }
`;

const HeadContent = styled.div`
  display: inline-block;
  width: 100%;
  padding-left: 30px;
  font-size: ${theme?.fontSize?.title3};
  font-weight: 600;
  color: ${theme.textColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const SelectBox = styled.div`
  height: 60px;
  line-height: 60px;
  box-shadow: 0 1px 2px ${theme.gray};
  position: relative;
  ${/* sc-selector */ Button} {
    position: absolute;
    top: 0;
    left: 0;
    width: 30px;
    height: 100%;
    border: none;
    i {
      color: ${theme.textColor};
    }
    &:focus {
      outline: none;
    }
  }
  .ant-input {
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    padding: 0;
    &:hover {
      border: none;
    }
    &:focus {
      box-shadow: none;
    }
  }
`;
const SelectDropdown = styled.div<{ calcHeight?: number }>`
  overflow: auto;
  height: ${(props) =>
    props.calcHeight
      ? `calc(100% - ${props.calcHeight}px)`
      : "calc(100% - 60px)"};
  margin: 0;
  padding: ${space}px 0;
`;

const ConfirmButton = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 16px 8px;
  background-color: ${theme.white};
  box-shadow: 0 -1px 2px 0 rgba(0, 0, 0, 0.2);
  ${/* sc-custom '.confirm-button'*/ Button} {
    width: 100%;
    height: 54px;
  }
`;
const GlobalStyle = createGlobalStyle`
.ant-modal-wrap.responsive-modal {
  animation: 'none';
  /* override antd default mobile max-width */
  @media (max-width: 767px) {
    .ant-modal {
      max-width: 100%;
    }
  }
  .ant-modal-content {
    height: 100%;
    border-radius: 0;
  }
}
.ant-select-dropdown {
  display: none;
  ${media.md`
      display: block;
      &.ant-select-dropdown-hidden {
        display: none;
      }
      .ant-select-dropdown-menu-item-selected {
        font-weight: 700 !important;
      }
  `};
}
`;
export const OptionItemText = styled.p`
  margin: 0;
  font-weight: 400;
  font-size: ${theme?.fontSize?.body};
  vertical-align: middle;
`;
export const OptionItemTitle = styled.p`
  margin: 0;
  font-weight: 400;
  font-size: ${theme?.fontSize?.caption};
  vertical-align: middle;
  color: ${theme.textDeepGray};
  margin-bottom: 8px;
`;
export const OptionItemSubText = styled.p`
  margin: 0;
  font-size: ${theme?.fontSize?.caption};
  font-weight: 400;
  color: ${theme.textDeepGray};
`;

const optionIconWidth = 28;
const optionIconMargin = 8;

export const OptionItem = styled.li<{
  wrapped?: boolean;
  hasIcon?: boolean;
  canSelect?: boolean;
}>`
  font-size: ${theme?.fontSize?.body};
  width: 100%;
  line-height: 1;
  ${({ wrapped }) =>
    wrapped &&
    css`
      line-height: 2;
    `};
  color: ${theme.textColor};
  ${/* sc-custom '.option-icon' */ Icon} {
    margin-right: ${optionIconMargin}px;
    font-size: ${optionIconWidth}px;
    font-weight: normal;
    vertical-align: middle;
    color: ${theme.textColor};
  }
  ${/* sc-custom '.option-item-text' */ OptionItemText} {
    ${({ hasIcon }) =>
      hasIcon &&
      css`
        width: calc(100% - ${optionIconMargin + optionIconWidth}px);
      `};
    ${({ wrapped }) =>
      !wrapped &&
      css`
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      `};
    display: inline-block;
  }
  ${({ canSelect }) =>
    canSelect === false
      ? css`
          padding: 10px 20px 0 20px;
        `
      : css`
          padding: 10px 20px;
          &:hover {
            background-color: ${theme.bgGray};
          }
        `}
`;
const ChildrenContainer = styled.div<{ isLessThanTablet: boolean }>`
  height: 100%;
  ${({ isLessThanTablet }) =>
    isLessThanTablet &&
    css`
      .date-range-selector.ant-calendar-picker {
        display: flex;
        .ant-calendar-range-picker-separator {
          display: none;
        }
        .ant-calendar-picker-icon {
          display: none;
        }
        .ant-calendar-range-picker-input {
          border: 1px solid ${theme.gray};
          border-radius: 2px;
          height: 40px;
          line-height: 40px;
          width: 100%;
        }
        .ant-calendar-picker-input.ant-input {
          display: flex !important;
          justify-content: space-between !important;
          padding: 0 !important;
          height: 100% !important;
          input:first-of-type {
            margin-right: 8px;
          }
        }
      }
    `};
`;

export const NoDropDownText = styled.div`
  font-weight: 400;
  line-height: 22px;
  font-size: ${theme?.fontSize?.body};
  margin: 20px 16px 0;
  ${media.sm`
    display: none;
  `};
`;

export interface ResponsiveModalProps {
  field: string;
  selectBox?: React.ReactNode;
  dropdownOptions?: React.ReactNode;
  disable?: boolean;
  placeholder?: string;
  selectLabel?: string;
  type?: "select" | "search";
  fieldValue?: string;
  getFieldDecorator?: WrappedFormUtils["getFieldDecorator"];
  isFormControl?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: any) => void;
  handleConfirmSelect?: () => void;
  confirmText?: string;
  isConfirm?: boolean;
  isAutoClose?: boolean;
  suffix?: React.ReactNode;
  noDropDownTip?: React.ReactNode;
  checkModalVisibility?: (isVisible: boolean) => void;
  isLessThanTablet?: boolean;
  className?: string;
  calcHeight?: number;
  noAnimation?: boolean;
}

export interface ResponsiveModalStates {
  visible: boolean;
  screenWidth: number;
}

class ResponsiveModal extends React.Component<
  ResponsiveModalProps,
  ResponsiveModalStates
> {
  static defaultProps = {
    disable: false,
    type: "select",
    isConfirm: false,
    isFormControl: false,
  };
  readonly state = {
    visible: false,
    screenWidth: 0,
  };
  private textInput: AntInput | null | undefined;

  private onWindowResize = debounce(() => {
    this.setState({ screenWidth: window.innerWidth });
  }, 300);
  public componentWillUnmount() {
    window.removeEventListener("resize", this.onWindowResize);
  }

  public componentDidMount() {
    this.setState({ screenWidth: window.innerWidth });
    window.addEventListener("resize", this.onWindowResize);
  }

  public componentDidUpdate() {
    if (this.state.visible && this.textInput) {
      this.textInput.focus();
    }
  }

  public render() {
    const {
      children,
      field,
      disable,
      getFieldDecorator,
      isFormControl,
      fieldValue,
      isConfirm,
      isAutoClose,
      confirmText,
      noDropDownTip,
      isLessThanTablet,
      className,
      calcHeight,
      noAnimation,
    } = this.props;
    return (
      <>
        <GlobalStyle />
        <Wrapper data-test={`field-${field}`} className={className}>
          <ChildrenContainer
            isLessThanTablet={!!isLessThanTablet}
            className="childrenContainer"
            onClick={() => !disable && this.open()}
          >
            {children}
          </ChildrenContainer>
          <Modal
            wrapClassName="responsive-modal"
            visible={this.state.visible}
            footer={null}
            closable={false}
            width="100%"
            style={{
              height: "100%",
              overflow: "hidden",
              top: 0,
              padding: 0,
              margin: 0,
              borderRadius: 0,
            }}
            bodyStyle={{ height: "100%", padding: 0 }}
            {...(noAnimation
              ? { transitionName: "none", maskTransitionName: "none" }
              : {})}
          >
            <SelectBox>
              <Button onClick={this.close} data-test={`${field}-mobile-back`}>
                <Icon type="arrow-left" />
              </Button>
              <HeadContent>
                {isFormControl && getFieldDecorator
                  ? getFieldDecorator(`mobile-${field}`, {
                      initialValue: fieldValue,
                    })(this.renderSelectBox())
                  : this.renderSelectBox()}
              </HeadContent>
            </SelectBox>
            {isEmpty(this.props.dropdownOptions) && (
              <NoDropDownText>{noDropDownTip}</NoDropDownText>
            )}
            <SelectDropdown
              onClick={() => (!isConfirm || isAutoClose) && this.close()}
              data-test={`${field}-mobile-dropdown`}
              calcHeight={calcHeight}
            >
              {this.props.dropdownOptions}
            </SelectDropdown>
            {isConfirm ? (
              <ConfirmButton>
                <Button
                  buttontype="primary"
                  onClick={() => this.handleConfirm()}
                  data-test={`${field}-mobile-confirm`}
                >
                  {confirmText}
                </Button>
              </ConfirmButton>
            ) : null}
          </Modal>
        </Wrapper>
      </>
    );
  }
  private renderSelectBox = () => {
    const {
      selectBox,
      placeholder,
      type,
      field,
      fieldValue,
      onChange,
      isFormControl,
      suffix,
    } = this.props;
    let InputOptions: InputProps & { ref: (input: AntInput) => AntInput } = {
      ref: (input: AntInput) => (this.textInput = input),
      type: "text",
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        onChange && onChange(e),
      autoFocus: true,
      autoComplete: "false",
      placeholder: placeholder,
      suffix: suffix,
    };
    if (!isFormControl) {
      InputOptions = {
        ...InputOptions,
        value: fieldValue,
      };
    }
    if (selectBox) {
      return selectBox;
    }
    return type === "search" ? (
      <AntInput
        onKeyDown={this.handleKeyDown}
        {...InputOptions}
        data-test={`${field}-mobile-input`}
      />
    ) : (
      <span>
        <strong>{this.props.selectLabel}</strong>
      </span>
    );
  };

  private handleKeyDown = (e: any) => {
    const { onKeyDown } = this.props;
    if (onKeyDown) {
      if (e.keyCode === KEYBOARD) {
        onKeyDown(e);
        this.close();
      }
    }
  };

  private handleConfirm = () => {
    const { handleConfirmSelect } = this.props;
    if (handleConfirmSelect) {
      handleConfirmSelect();
    }
    this.close();
  };
  private open = () => {
    if (isTablet(this.state.screenWidth)) {
      this.setState({ visible: true }, () => {
        const { checkModalVisibility } = this.props;
        if (checkModalVisibility) {
          checkModalVisibility(true);
        }
      });
    }
  };

  private close = () => {
    if (this.state.visible) {
      this.setState({ visible: false }, () => {
        const { checkModalVisibility } = this.props;
        if (checkModalVisibility) {
          checkModalVisibility(false);
        }
      });
    }
  };
}

export { ResponsiveModal };
